import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Phone, Clock, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, ORDER_STATUS_LABELS, ORDER_STATUS_BADGE } from "@/api";

const FILTERS = [{ id: "all", label: "Tutte" }, ...Object.entries(ORDER_STATUS_LABELS).map(([id, label]) => ({ id, label }))];

export default function OrdersPanel({ orders, filter, setFilter, onRefresh }) {
  const changeStatus = async (orderId, status) => {
    try {
      await api.patch(`/orders/${orderId}`, { status });
      toast.success("Stato aggiornato");
      onRefresh();
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const visible = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div data-testid="orders-panel">
      <div className="flex flex-wrap gap-2 mb-6" data-testid="order-status-filters">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`order-filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === f.id ? "bg-sky-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-sky-300"
            }`}>
            {f.label}
          </button>
        ))}
      </div>
      {visible.length === 0 ? (
        <p className="text-sm text-slate-500 py-12 text-center" data-testid="orders-empty">Nessuna richiesta d'ordine in questa categoria.</p>
      ) : (
        <div className="space-y-4">
          {visible.map((o) => (
            <div key={o.id} className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid={`order-card-${o.id}`}>
              <div className="flex flex-col md:flex-row md:items-start gap-4 justify-between">
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-slate-900" data-testid={`order-customer-${o.id}`}>{o.customer_name}</h3>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${ORDER_STATUS_BADGE[o.status]}`} data-testid={`order-status-badge-${o.id}`}>
                      {ORDER_STATUS_LABELS[o.status]}
                    </span>
                  </div>
                  <p className="text-sm text-sky-700 font-medium">{o.machine_name}</p>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
                    <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {o.customer_email}</span>
                    <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {o.customer_phone}</span>
                    {o.preferred_time && (
                      <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {o.preferred_time === "mattina" ? "Mattina (9:00-12:30)" : "Pomeriggio (15:00-18:30)"}</span>
                    )}
                  </div>
                  {o.notes && (
                    <p className="flex items-start gap-1.5 text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-2 mt-1">
                      <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {o.notes}
                    </p>
                  )}
                  <p className="text-xs text-slate-400">
                    Ricevuta il {new Date(o.created_at).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                <div className="shrink-0">
                  <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                    <SelectTrigger className="w-[170px] bg-white" data-testid={`order-status-select-${o.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ORDER_STATUS_LABELS).map(([v, l]) => (
                        <SelectItem key={v} value={v} data-testid={`order-status-option-${o.id}-${v}`}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
